import {
	PageInfo,
	ProjectTimeApproval,
	useDeleteTimeApprovalMutation,
	useGetMyTimeApprovalsByProjectLazyQuery,
} from "@repo/server-api";
import { useNotification } from "@repo/shared/components";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { DataSource } from "@repo/legacy/components/shared/lists/DataSource";
import { PaginationModel } from "@repo/legacy/components/shared/lists/PaginationModel";

export function useMyTimeApprovalsByProjectDataSource(
	projectId: string | null,
): DataSource<ProjectTimeApproval> {
	const projectIdRef = useRef(projectId);

	useEffect(() => {
		projectIdRef.current = projectId;
	}, [projectId]);
	const [rows, setRows] = useState<ProjectTimeApproval[]>([]);
	const [rowCount, setRowCount] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [, setPageInfo] = useState<PageInfo | null>(null);
	const [startDate, setStartDate] = useState<string>("");
	const [endDate, setEndDate] = useState<string>("");
	const [pageModal, setPageModal] = useState<PaginationModel>({
		page: 0,
		pageSize: 10,
	});
	const { t } = useTranslation();

	const { showNotification } = useNotification();

	const [deleteTimeApproval] = useDeleteTimeApprovalMutation({
		onCompleted(data) {
			if (data && data.deleteTimeApproval.boolean) {
				showNotification(t("projects.timeSheets.deleteSuccess"), "success");
				refresh();
			} else if (data && data.deleteTimeApproval.errors) {
				data.deleteTimeApproval.errors.forEach((error) => {
					showNotification(error.message, "error");
				});
			}
		},
		onError(error) {
			showNotification(error.message, "error");
		},
	});

	const [getMyTimeApprovalsByProject] = useGetMyTimeApprovalsByProjectLazyQuery({
		fetchPolicy: "no-cache",
		onCompleted(data) {
			if (data && data.myTimeApprovalsByProject) {
				setRows(data.myTimeApprovalsByProject.nodes as ProjectTimeApproval[]);
				setRowCount(data.myTimeApprovalsByProject.totalCount);
				setPageInfo(data.myTimeApprovalsByProject.pageInfo);
				setIsLoading(false);
			}
		},
		onError() {
			setIsLoading(false);
		},
	});

	const refresh = () => {
		fetchDataStartEnd(pageModal, startDate, endDate);
	};

	const fetchDataStartEnd = (page: PaginationModel, start: string, end: string) => {
		setIsLoading(true);
		setPageModal(page);
		setStartDate(start);
		setEndDate(end);

		// Only fetch if projectId is provided
		if (!projectIdRef.current) {
			setRows([]);
			setRowCount(0);
			setIsLoading(false);
			return;
		}

		getMyTimeApprovalsByProject({
			variables: {
				ProjectId: projectIdRef.current,
				after: (page.pageSize * page.page).toString(),
				before: null,
				end: dayjs(end).endOf("year").format("YYYY-MM-DD"),
				first: page.pageSize,
				last: null,
				start: dayjs(start).startOf("year").format("YYYY-MM-DD"),
				search: "",
			},
		})
			.then((response) => {
				if (response.data && response.data.myTimeApprovalsByProject) {
					setRows(response.data.myTimeApprovalsByProject.nodes as ProjectTimeApproval[]);
					setRowCount(response.data.myTimeApprovalsByProject.totalCount);
					setPageInfo(response.data.myTimeApprovalsByProject.pageInfo);
					setIsLoading(false);
				}
			})
			.catch(() => {
				setIsLoading(false);
			});
	};

	const deleteDataWithReason = (id: string, reason: string) => {
		deleteTimeApproval({
			variables: {
				projectTimeApprovalId: id,
				description: reason,
			},
		});
	};

	return {
		rows,
		rowCount,
		isLoading,
		refresh,
		fetchDataStartEnd,
		deleteDataWithReason,
	};
}
